
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import devices from '@/utilities/devices';

export interface TitleProps {
	/**
	 * Defines the CSS text alignment property.
	 */
	readonly align?: 'center' | 'left' | 'right';
	/**
	 * Child of the component.
	 */
	readonly children: string | any;
	/**
	 * Used to modify extended component.
	 */
	readonly className?: any;
	/**
	 * Defines the CSS font-size property.
	 */
	readonly fontSize?: string;
	/**
	 * Defines the CSS padding property.
	 */
	readonly padding?: string;
	/**
	 * Defines the color of the underline.
	 */
	readonly underlineColor?: 'red' | 'blue' | 'prune' | 'beige';
	/**
	 * Defines the color of the text.
	 */
	readonly white?: boolean;
	/**
	 * Defines the CSS width property.
	 */
	readonly width?: string;
}

interface TitleStyledProps extends TitleProps {
	/**
	 * Returns if the title is in the viewport, or not.
	 */
	readonly isVisible: boolean;
	/**
	 * Defines the color of the underline.
	 */
	readonly underlineColor?: 'red' | 'blue' | 'prune' | 'beige';
}

// #region STYLE
const TitleContainer = styled.h2<TitleStyledProps>`
	color: ${({ theme, white }) => white ? theme.color.white : theme.color.prune};
	font-size: ${({ theme, fontSize }) => fontSize ? fontSize : theme.fontSize.HEADLINE};
	margin: 0 ${({ align }) => align === 'center' && 'auto'};
	padding: ${({ padding }) => padding};
	text-align: ${({ align }) => align};
	width: ${({ width }) => width};
	em {
		display: inline-block;
		font-style: normal;
		:after {
			background-color: ${({ underlineColor, theme }) => underlineColor ? theme.color[underlineColor] : theme.color.red};
			border-radius: 10px;
			content: '';
			display: block;
			filter: drop-shadow(${({ underlineColor, theme }) => {
				switch (underlineColor) {
					case 'red':
						return `0rem .1rem .2rem rgba(${theme.color.rgbRed}, .4)`;
					case 'blue':
						return `0rem .1rem .2rem rgba(${theme.color.rgbBlue}, .4)`;
					case 'prune':
						return `0rem .1rem .2rem rgba(${theme.color.rgbPrune}, .4)`;
					case 'beige':
						return `0rem .1rem .2rem rgba(${theme.color.rgbBeige}, .4)`;
				}
			}});
			height: 4px;
			margin-top: -4px;
			transition: all 1s cubic-bezier(0.230, 1.000, 0.320, 1.000) .2s;
			width: ${({ isVisible }) => isVisible ? '100%' : '0%'};
		}
	}

	@media screen and (${devices.mobile}) {
		font-size: ${({ theme }) => theme.fontSize.XXLARGE};
		padding: .5em 0em .5em;
		text-align: center;
		white-space: pre-line;
		width: 100%;
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		font-size: ${({ theme }) => theme.fontSize.HEADLINE};
		padding: 1em 0em .5em;
		text-align: center;
		white-space: pre-line;
		width: 100%;
	}
`;
// #endregion

const Title = ({ className, children, fontSize, underlineColor, white, width, align, padding }: TitleProps) => {

	const [ref, inView] = useInView({
		rootMargin: '-150px 0px',
		triggerOnce: true,
	});

	return (
		<TitleContainer
			align={align}
			className={className}
			fontSize={fontSize}
			isVisible={inView}
			padding={padding}
			ref={ref}
			underlineColor={underlineColor}
			white={white}
			width={width}
		>
			{children}
		</TitleContainer>
	);
};

export default Title;
